import React, { useEffect, useRef, useState } from "react";
import { Camera } from "@mediapipe/camera_utils";
import "./style.css";

function PhotoFrame() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const previewImageRef = useRef(null);
  const previewModalRef = useRef(null);
  const [currentFacing, setCurrentFacing] = useState("user");
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const cameraRef = useRef(null);
  const [currentFrame, setCurrentFrame] = useState(1); // フレームIDで管理
  const [showFrameModal, setShowFrameModal] = useState(true);
  const [firstFlg, setFirstFlg] = useState(false);

  // フレームオプションの定義
  const frameOptions = [
    {
      id: 1,
      samplePath: "/frame/sample_frame_1.png",
      topPath: "/frame/frame_1_top.png",
      bottomPath: "/frame/frame_1_bottom.png",
    },
    {
      id: 2,
      samplePath: "/frame/sample_frame_2.png",
      topPath: "/frame/frame_2_top.png",
      bottomPath: "/frame/frame_2_bottom.png",
    },
    {
      id: 3,
      samplePath: "/frame/sample_frame_3.png",
      topPath: "/frame/frame_3_top.png",
      bottomPath: "/frame/frame_3_bottom.png",
    },
  ];

  // フレーム画像を事前読み込みするための状態 - 上下別々に管理
  const [frameImages, setFrameImages] = useState({});

  // フレーム画像の事前読み込み
  useEffect(() => {
    const loadFrameImages = async () => {
      const loadedImages = {};

      for (const frame of frameOptions) {
        loadedImages[frame.id] = {
          sample: null,
          top: null,
          bottom: null,
        };

        // サンプル画像の読み込み
        const sampleImg = new Image();
        sampleImg.src = frame.samplePath;
        await new Promise((resolve) => {
          sampleImg.onload = resolve;
          sampleImg.onerror = resolve;
        });
        loadedImages[frame.id].sample = sampleImg;

        // 上部フレーム画像の読み込み
        const topImg = new Image();
        topImg.src = frame.topPath;
        await new Promise((resolve) => {
          topImg.onload = resolve;
          topImg.onerror = resolve;
        });
        loadedImages[frame.id].top = topImg;

        // 下部フレーム画像の読み込み
        const bottomImg = new Image();
        bottomImg.src = frame.bottomPath;
        await new Promise((resolve) => {
          bottomImg.onload = resolve;
          bottomImg.onerror = resolve;
        });
        loadedImages[frame.id].bottom = bottomImg;
      }

      setFrameImages(loadedImages);

      // フレーム画像の読み込みが完了したら描画を更新
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      startDrawLoop();
    };

    loadFrameImages();
  }, []);

  // currentFrameが変更されたときに描画を更新するuseEffect
  useEffect(() => {
    // フレーム画像が読み込まれている場合のみ描画ループを再開始
    if (Object.keys(frameImages).length > 0) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      startDrawLoop();
    }
  }, [currentFrame, frameImages]);

  const handleCapture = () => {
    const canvas = canvasRef.current;
    const photoUrl = canvas.toDataURL("image/jpeg", 0.95);
    previewImageRef.current.src = photoUrl;
    previewModalRef.current.style.display = "block";
  };

  const handleRetake = () => {
    previewModalRef.current.style.display = "none";
  };

  const handleBack = () => {
    window.location.href = "/";
  };

  // カメラストリームを停止する関数
  const stopCurrentStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  // カメラを開始する関数
  const startCamera = async (facingMode) => {
    try {
      if (cameraRef.current) {
        await cameraRef.current.stop();
      }

      const camera = new Camera(videoRef.current, {
        onFrame: async () => {
          if (!videoRef.current || videoRef.current.readyState !== 4) return;
        },
        width: 1920,
        height: 1080,
        facingMode: facingMode,
      });

      await camera.start();
      cameraRef.current = camera;
    } catch (error) {
      console.error("カメラの開始に失敗しました:", error);
    }
  };

  // レスポンシブな描画計算を行う関数
  const calculateResponsiveDimensions = (
    screenWidth,
    screenHeight,
    frameImages
  ) => {
    const currentFrameImages = frameImages[currentFrame];
    if (
      !currentFrameImages ||
      !currentFrameImages.top ||
      !currentFrameImages.bottom
    ) {
      return {
        topFrame: { x: 0, y: 0, width: screenWidth, height: 0 },
        bottomFrame: { x: 0, y: screenHeight, width: screenWidth, height: 0 },
        videoArea: { x: 0, y: 0, width: screenWidth, height: screenHeight },
      };
    }

    const topImg = currentFrameImages.top;
    const bottomImg = currentFrameImages.bottom;

    // フレーム画像の元のサイズから比率を計算
    const topAspectRatio = topImg.naturalWidth / topImg.naturalHeight;
    const bottomAspectRatio = bottomImg.naturalWidth / bottomImg.naturalHeight;

    // 画面幅に合わせてフレームのサイズを計算
    const topFrameWidth = screenWidth;
    const topFrameHeight = topFrameWidth / topAspectRatio;

    const bottomFrameWidth = screenWidth;
    const bottomFrameHeight = bottomFrameWidth / bottomAspectRatio;

    // 上下のフレームの配置
    const topFrame = {
      x: 0,
      y: 0,
      width: topFrameWidth,
      height: topFrameHeight,
    };

    const bottomFrame = {
      x: 0,
      y: screenHeight - bottomFrameHeight,
      width: bottomFrameWidth,
      height: bottomFrameHeight,
    };

    // ビデオエリア（フレーム間の部分）
    const videoArea = {
      x: 0,
      y: topFrameHeight,
      width: screenWidth,
      height: screenHeight - topFrameHeight - bottomFrameHeight,
    };

    return { topFrame, bottomFrame, videoArea };
  };

  // 描画ループを開始する関数（レスポンシブ対応版）
  const startDrawLoop = () => {
    const drawFrame = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (!video || !canvas || video.readyState !== 4) {
        animationFrameRef.current = requestAnimationFrame(drawFrame);
        return;
      }

      const ctx = canvas.getContext("2d");
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      // Canvasサイズを画面サイズに設定
      canvas.width = screenWidth;
      canvas.height = screenHeight;

      // 背景を黒で塗りつぶし
      ctx.fillStyle = "black";
      ctx.fillRect(0, 0, screenWidth, screenHeight);

      // ビデオのサイズ
      const vw = video.videoWidth;
      const vh = video.videoHeight;

      if (currentFrame && frameImages[currentFrame]) {
        const dimensions = calculateResponsiveDimensions(
          screenWidth,
          screenHeight,
          frameImages
        );
        const { topFrame, bottomFrame, videoArea } = dimensions;

        // ビデオを全画面に描画（背景として）
        ctx.save();

        // カメラ向きによる左右反転処理
        if (currentFacing === "environment") {
          ctx.translate(screenWidth, 0);
          ctx.scale(-1, 1);
        }

        if (!firstFlg) {
          ctx.translate(screenWidth, 0);
          ctx.scale(-1, 1);
          setFirstFlg(true);
        }

        // ビデオを画面全体をカバーするように描画
        const videoAspectRatio = vw / vh;
        const screenAspectRatio = screenWidth / screenHeight;

        let sx, sy, sw, sh;

        if (videoAspectRatio > screenAspectRatio) {
          sh = vh;
          sw = vh * screenAspectRatio;
          sx = (vw - sw) / 2;
          sy = 0;
        } else {
          sw = vw;
          sh = vw / screenAspectRatio;
          sx = 0;
          sy = (vh - sh) / 2;
        }

        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, screenWidth, screenHeight);

        ctx.restore();

        // 上部フレームを描画
        const topImg = frameImages[currentFrame].top;
        if (topImg && topFrame.height > 0) {
          ctx.drawImage(
            topImg,
            topFrame.x,
            topFrame.y,
            topFrame.width,
            topFrame.height
          );
        }

        // 下部フレームを描画
        const bottomImg = frameImages[currentFrame].bottom;
        if (bottomImg && bottomFrame.height > 0) {
          ctx.drawImage(
            bottomImg,
            bottomFrame.x,
            bottomFrame.y,
            bottomFrame.width,
            bottomFrame.height
          );
        }
      } else {
        // フレームがない場合は通常のビデオ描画
        ctx.save();

        if (currentFacing === "user") {
          ctx.translate(screenWidth, 0);
          ctx.scale(-1, 1);
        }

        const videoAspectRatio = vw / vh;
        const screenAspectRatio = screenWidth / screenHeight;

        let sx, sy, sw, sh;

        if (videoAspectRatio > screenAspectRatio) {
          sh = vh;
          sw = vh * screenAspectRatio;
          sx = (vw - sw) / 2;
          sy = 0;
        } else {
          sw = vw;
          sh = vw / screenAspectRatio;
          sx = 0;
          sy = (vh - sh) / 2;
        }

        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, screenWidth, screenHeight);

        ctx.restore();
      }

      animationFrameRef.current = requestAnimationFrame(drawFrame);
    };

    animationFrameRef.current = requestAnimationFrame(drawFrame);
  };

  const handleSwitchCamera = async () => {
    stopCurrentStream();
    const newFacing = currentFacing === "user" ? "environment" : "user";
    setCurrentFacing(newFacing);
    await startCamera(newFacing);
    startDrawLoop();
  };

  // フレーム選択ハンドラー（IDベースに修正）
  const handleFrameSelect = (frameId) => {
    setCurrentFrame(frameId);
    setShowFrameModal(false);
  };

  useEffect(() => {
    startCamera(currentFacing);
    return () => {
      stopCurrentStream();
    };
  }, []);

  return (
    <div
      style={{
        height: "100dvh",
        width: "100vw",
        overflow: "hidden",
        backgroundColor: "rgba(170, 227, 249, 0.549)",
        position: "relative",
        margin: 0,
        padding: 0,
        boxSizing: "border-box",
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          visibility: "hidden",
        }}
      />

      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100dvh",
          zIndex: 10,
          display: "block",
        }}
      />

      <button className="switch-camera-btn" onClick={handleSwitchCamera}>
        <img src="/change.png" alt="カメラ切り替え" />
      </button>

      <button className="capture-btn" onClick={handleCapture}>
        <img src="/camera_icon.svg" alt="撮影" />
      </button>

      {/* フレーム選択モーダル */}
      {showFrameModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100dvh",
            zIndex: 100,
            background: "rgba(0,0,0,0.95)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            boxSizing: "border-box",
            margin: 0,
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.1)",
              padding: "30px",
              borderRadius: "20px",
              maxWidth: "98%",
              maxHeight: "95%",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "30px",
              alignItems: "center",
              margin: 0,
            }}
          >
            <h3
              style={{
                color: "white",
                margin: "0 0 30px 0",
                fontSize: "32px",
                fontWeight: "bold",
                textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                textAlign: "center",
              }}
            >
              フレームを選択
            </h3>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "36px",
                width: "100%",
                maxWidth: "1400px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {frameOptions.map((frame) => (
                <div
                  key={frame.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "18px",
                    cursor: "pointer",
                    padding: "0",
                    borderRadius: "0",
                    background: "none",
                    border: "none",
                    transition: "all 0.3s ease",
                    width: "100%",
                    maxWidth: "280px",
                  }}
                  onClick={() => handleFrameSelect(frame.id)}
                >
                  <img
                    src={frame.samplePath}
                    style={{
                      width: "100%",
                      maxWidth: "280px",
                      height: "auto",
                      borderRadius: "0",
                      border: "none",
                      transition: "box-shadow 0.3s, outline 0.3s",
                      display: "block",
                      margin: "0 auto",
                    }}
                    alt={`フレーム${frame.id}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div
        ref={previewModalRef}
        className="preview-modal"
        style={{ display: "none" }}
      >
        <div className="modal-content">
          <img
            ref={previewImageRef}
            alt="プレビュー"
            className="preview-image"
          />
          <div className="save-instruction">画像を長押しで保存できます</div>
          <div className="button-container">
            <button onClick={handleRetake} className="retake-btn">
              再撮影
            </button>
            <button onClick={handleBack} className="back-btn">
              トップに戻る
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhotoFrame;
